import { useMemo, useState } from 'react';

import Cookies from 'js-cookie';

import type { Property } from '@ts-types/property';

import { useDemoMinskogStore } from '@store/DemoMinskogStore';
import { usePropsDataStore } from '@store/PropsDataStore';
import { useVisaValdaStore } from '@store/VisaValdaStore';

import { Button, Icon, Sheet, Text } from '@ui-component-library';

import { usePlusBlockerDialog } from '@components/PlusBlockerDialog/PlusBlockerDialog.context';

import { cn } from '../../../utils/cn';

import { AlertDialog } from './components/AlertModal';
import { PopOverItem, PopoverMenu } from './components/PopoverMenu';
import { PropertyRow } from './components/PropertyRow';
import { SearchAndAddProperties } from './components/SearchAndAddProperties';
import UploadForestandFile from './components/UploadForestandFile/UploadForestandFile';
import {
  PropertySelectionProvider,
  usePropertySelection
} from './context/PropertySelectionContext';
import type { PropertySelectorProps } from './PropertySelector.types';
import { municipalityFromDesignation } from './PropertySelector.utils';

const PropertySelectorContent = ({
  className,
  isOpen,
  onClose,
  openAddPropertyInitially = false
}: PropertySelectorProps) => {
  const { props: properties, setProps } = usePropsDataStore();
  const { setVisaValda } = useVisaValdaStore();
  const hasPlusAccess = useMemo(() => properties.some((p) => p.dfr), [properties]);
  const { openPlusBlockerDialog } = usePlusBlockerDialog();
  const {
    pendingActivePropertyRefs,
    selectAll,
    deselectAll,
    areAllSelected,
    applyChanges,
    resetPendingChanges,
    alertMessage,
    setAlertMessage
  } = usePropertySelection();

  const [isSearchAndAddOpen, setIsSearchAndAddOpen] = useState(() => openAddPropertyInitially);
  const [isUploadForestandDrawerOpen, setIsUploadForestandDrawerOpen] = useState(false);
  const activePropertyRefs = useMemo(
    () => new Set(properties.filter((p) => p.active).map((p) => p.ref)),
    [properties]
  );

  const enrichedProperties = useMemo(
    () =>
      properties
        .map((prop) => ({
          ...prop,
          active: pendingActivePropertyRefs.has(prop.ref)
        }))
        .sort((a, b) => {
          // Primarily sorts active properties in top
          const aIsActive = activePropertyRefs.has(a.ref);
          const bIsActive = activePropertyRefs.has(b.ref);

          if (aIsActive && !bIsActive) {
            return -1;
          }
          if (!aIsActive && bIsActive) {
            return 1;
          }

          return a?.designation?.localeCompare(b?.designation ?? '', 'sv', { sensitivity: 'base' });
        }),
    [properties, pendingActivePropertyRefs, activePropertyRefs]
  );

  const syncActivePropertyCookie = (activeProperties: Property[]) => {
    const activeRefs = activeProperties
      .filter((p) => p.data?.sections?.length <= 300)
      .map((p) => p.ref)
      .join(',');
    Cookies.set('activeProps', activeRefs);
  };

  const handleClose = () => {
    resetPendingChanges();
    onClose();
  };

  const applyPendingSelection = (updatedProperties: Property[]) => {
    setProps(updatedProperties);
    syncActivePropertyCookie(updatedProperties.filter((p) => p.active));
    setVisaValda(true);
    resetPendingChanges();
  };

  const handleConfirmSelection = async () => {
    const selectedProperties = properties.filter((prop) => pendingActivePropertyRefs.has(prop.ref));
    const hasPlusSelected = selectedProperties.some((p) => p.dfr);
    const hasNonPlusSelected = selectedProperties.some((p) => !p.dfr);

    if (hasPlusSelected && hasNonPlusSelected) {
      const plusProperties = selectedProperties.filter((p) => p.dfr);
      const nonPlusProperties = selectedProperties.filter((p) => !p.dfr);

      setAlertMessage({
        title: 'Du kan inte visa dessa fastigheter samtidigt',
        subtitle: (
          <>
            Fastigheter <span className="font-medium">utan Plus</span> kan inte visas tillsammans
            med fastigheter <span className="font-medium">med Treebula Plus</span>.
          </>
        ),
        content: {
          accordions: [
            {
              label: `${plusProperties.length} fastigheter med Plus visas`,
              items: plusProperties.map((p) => p.designation)
            },
            {
              label: `${nonPlusProperties.length} fastigheter utan Plus döljs`,
              items: nonPlusProperties.map((p) => p.designation)
            }
          ]
        },
        primaryActionText: `Visa ${plusProperties.length} ${plusProperties.length === 1 ? 'fastighet' : 'fastigheter'} med Plus`,
        onPrimaryAction: () => {
          setAlertMessage(null);
          applyPendingSelection(
            properties.map((prop) => ({
              ...prop,
              active: prop.dfr && pendingActivePropertyRefs.has(prop.ref)
            }))
          );
          onClose();
        },
        isOpen: true,
        onClose: () => {
          setAlertMessage(null);
        }
      });
      return;
    }

    applyPendingSelection(applyChanges());

    if (selectedProperties.length > 0) {
      localStorage.setItem(
        'woodprices_muni',
        municipalityFromDesignation(selectedProperties[0].designation)
      );
    }

    onClose();
  };

  return (
    <>
      <div className={cn('', className)}>
        <Sheet isOpen={isOpen} onClose={handleClose}>
          <Sheet.Header className="items-center">
            <div>
              <Text variant="text-lg/leading-normal/medium">Mina fastigheter</Text>
              <Text variant="text-sm/leading-normal/medium" className="text-[#737373]">
                Välj och hantera
              </Text>
            </div>
            <div className="flex flex-row items-center justify-end gap-3">
              <PopoverMenu icon="PlusSimple" title="Lägg till fastighet">
                <div className="flex flex-col gap-1">
                  <PopOverItem
                    icon="MagnifyingGlassList"
                    label="Sök i Lantmäteriet"
                    onClick={() => setIsSearchAndAddOpen(true)}
                  />
                  <div className="h-px w-full bg-[#E6E6E6]/60" />
                  <PopOverItem
                    icon="UploadSimple"
                    label="Ladda upp"
                    onClick={() =>
                      hasPlusAccess
                        ? setIsUploadForestandDrawerOpen(true)
                        : openPlusBlockerDialog('forstand-file')
                    }
                    isPlus={hasPlusAccess}
                  />
                </div>
              </PopoverMenu>
              <PopoverMenu icon="ThreeDotsSimple" title="Mina fastigheter">
                <div className="flex flex-col gap-1">
                  <PopOverItem icon="Checks" label="Välj alla" onClick={selectAll} />
                  <PopOverItem icon="MinusSquare" label="Avmarkera alla" onClick={deselectAll} />
                  <div className="h-px w-full bg-[#E6E6E6]/60" />
                  <PopOverItem
                    icon="Question"
                    label="Hjälp & support"
                    onClick={() => {
                      window.open('/kontakta-oss/', '_blank');
                    }}
                  />
                </div>
              </PopoverMenu>
            </div>
          </Sheet.Header>
          <Sheet.Content className="relative p-0">
            <div className="bg-white-50 sticky top-0 left-0 z-30 flex w-full flex-row items-center justify-between gap-4 px-4 py-2">
              <Text variant="text-xs/leading-normal/medium" className="text-[#737373]">
                {pendingActivePropertyRefs.size} av {properties.length} valda
              </Text>
              <Button
                variant="tertiary"
                size="small"
                className="whitespace-nowrap"
                onClick={areAllSelected ? deselectAll : selectAll}
              >
                {areAllSelected ? 'Avmarkera alla' : 'Välj alla'}
              </Button>
            </div>
            {enrichedProperties.map((property) => {
              return (
                <PropertyRow key={property.ref} property={property} isActive={property.active} />
              );
            })}
            <PopoverMenu
              icon="PlusSimple"
              title="Lägg till fastighet"
              trigger={
                <div
                  className={
                    'flex cursor-pointer flex-row items-center gap-4 border-b border-solid border-[#E6E6E6] px-4 py-3 hover:bg-[#F5F5F5]'
                  }
                >
                  <Icon name="PlusSimple" size="large" className="" />
                  <Text variant="text-sm/leading-normal/medium" className="flex min-w-0 flex-1">
                    Lägg till fastighet
                  </Text>
                  <div />
                  <Icon name="ArrowShortSimple" size="large" className="h-6 w-6 rotate-180" />
                </div>
              }
            >
              <div className="flex flex-col gap-1">
                <PopOverItem
                  icon="MagnifyingGlassList"
                  label="Sök i Lantmäteriet"
                  onClick={() => setIsSearchAndAddOpen(true)}
                />
                <div className="h-px w-full bg-[#E6E6E6]/60" />
                <PopOverItem
                  icon="UploadSimple"
                  label="Ladda upp"
                  onClick={() =>
                    hasPlusAccess
                      ? setIsUploadForestandDrawerOpen(true)
                      : openPlusBlockerDialog('forstand-file')
                  }
                  isPlus={hasPlusAccess}
                />
              </div>
            </PopoverMenu>
            {!hasPlusAccess && (
              <div className="flex cursor-pointer flex-row justify-between gap-3 border-b border-solid border-[#E6E6E6] px-4 hover:bg-[#F5F5F5]">
                <div
                  className="flex w-full flex-row items-center gap-3 py-3"
                  onClick={() => window.open('/min-skog/demo/', '_blank')}
                >
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/medium">Utforska upplåst läge</Text>
                    <div className="flex flex-row items-center justify-start gap-1">
                      <Text variant="text-xs/leading-normal/medium" className="text-[#737373]">
                        Nyfiken på hur allt ser ut efter man betalt? Klicka här
                      </Text>
                    </div>
                  </div>
                </div>
                <div className="flex flex-row items-center justify-end gap-4 py-3">
                  <Icon name="ArrowShortSimple" size="large" className="h-6 w-6 rotate-180" />
                </div>
              </div>
            )}
          </Sheet.Content>
          <Sheet.Footer className="flex flex-row justify-between gap-3">
            <Button fullWidth variant="secondary" onClick={handleClose}>
              Stäng
            </Button>
            <Button fullWidth variant="primary" onClick={handleConfirmSelection}>
              <Text variant="text-sm/leading-normal/medium">Klar</Text>
              {pendingActivePropertyRefs.size > 0 && (
                <span className="relative flex h-5 min-w-5 items-center justify-center rounded-full bg-[#F5F5F5]">
                  <span className="absolute left-[5px] min-w-[7.5px] text-xs leading-none font-medium text-[#1A1A1A]">
                    {pendingActivePropertyRefs.size}
                  </span>
                </span>
              )}
            </Button>
          </Sheet.Footer>
        </Sheet>
      </div>
      <SearchAndAddProperties
        isOpen={isSearchAndAddOpen}
        onClose={() => setIsSearchAndAddOpen(false)}
        onAddSuccess={() => setIsSearchAndAddOpen(false)}
      />
      <UploadForestandFile
        isOpen={isUploadForestandDrawerOpen}
        onClose={() => setIsUploadForestandDrawerOpen(false)}
      />

      {alertMessage && <AlertDialog {...alertMessage} isOpen={!!alertMessage} size="small" />}
    </>
  );
};

const PropertySelector = ({
  className,
  isOpen,
  onClose,
  openAddPropertyInitially = false
}: PropertySelectorProps) => {
  const { props: properties } = usePropsDataStore();
  const { isDemo } = useDemoMinskogStore();

  if (isDemo) {
    return null;
  }

  return (
    <>
      <PropertySelectionProvider properties={properties}>
        <PropertySelectorContent
          className={className}
          isOpen={isOpen}
          onClose={onClose}
          openAddPropertyInitially={openAddPropertyInitially}
        />
      </PropertySelectionProvider>
    </>
  );
};

export default PropertySelector;
